// deployment_class: customer_env_allowed
/**
 * RequireScope — Plan 14 §Task 5 + Spec 12 §5.2 + §6.5. Wraps a route that
 * requires a specific OAuth scope. If the user lacks the scope, renders the
 * friendly PortalNotAccessible page (NOT a generic 403). If unauthenticated,
 * defers to RequireAuth's redirect-to-login behavior.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { PortalNotAccessible } from '../components/PortalNotAccessible';

export function RequireScope({
  scope,
  children,
}: {
  scope: string;
  children: ReactNode;
}): JSX.Element {
  const { status, scopes } = useAuth();
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    return <div className="min-h-screen bg-paper" aria-busy="true" />;
  }

  if (status !== 'authenticated') {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?returnTo=${returnTo}`} replace />;
  }

  if (!scopes.includes(scope)) {
    return <PortalNotAccessible scope={scope} />;
  }

  return <>{children}</>;
}
